import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';
import { message } from 'antd';
import { maxFileSize } from '../../../../../../constants/maxFileSize';
import { getBlobUrl } from '../../../../../../helpers/getBlobUrl';
import { getRoomAttachment } from '../../../../requests/getRoomAttachment';
import useDeleteRoomAttachmentMutation from '../../../../mutations/useDeleteRoomAttachmentMutation';
import useAddRoomAttachmentsMutation from '../../../../mutations/useAddRoomAttachmentsMutation';
import { roomQueryKeys } from '../../../../queries/queryKeys';
import OfficeDocuments from '../../../../components/OfficeDocuments/OfficeDocuments';
import { getEditingRoomData } from '../../../../utils/getEditingRoomData';

const DocumentsBlock = ({ room }) => {
  const queryClient = useQueryClient();

  const userCanEdit = useSelector(
    (store) => store.authentication.userInfo.CanCreateOffice
  );

  const userCanDelete = useSelector(
    (store) => store.authentication.userInfo.CanDeleteOffice
  );

  const handleMutationSuccess = () =>
    queryClient.invalidateQueries(roomQueryKeys(room?.id));

  const {
    mutateAsync: deleteAttachmentMutateAsync,
    isLoading: isDeleteAttachmentLoading,
  } = useDeleteRoomAttachmentMutation({
    onSuccess: handleMutationSuccess,
  });

  const { mutate: addAttachmentMutate, isLoading: isAddAttachmentLoading } =
    useAddRoomAttachmentsMutation({
      onSuccess: handleMutationSuccess,
    });

  const handleDeleteAttachment = useCallback(
    async (attachment) => {
      const data = getEditingRoomData(room);

      await deleteAttachmentMutateAsync({
        attachmentId: attachment.id,
        room: data,
      });
    },
    [room, deleteAttachmentMutateAsync]
  );

  const handleAddAttachment = useCallback(
    ({ file }) => {
      addAttachmentMutate({ roomId: room?.id, files: [file] });
    },
    [room, addAttachmentMutate]
  );

  const handleBeforeUpload = (file) => {
    if (file.size > maxFileSize) {
      message.error('Файл должен весить не больше 15 мб');
      return false;
    }

    return true;
  };

  const getAttachmentUrl = useCallback(
    async (data) => {
      const fileName = typeof data === 'string' ? data : data.fileName;
      const fileExtension = fileName.split('.')[1];

      const attachment = await getRoomAttachment(room?.id, fileName);

      if (attachment) {
        const url = getBlobUrl(attachment, fileExtension);
        return url;
      }

      return;
    },
    [room]
  );

  return (
    <OfficeDocuments
      documents={room?.attachments}
      onDeleteDocument={userCanDelete && handleDeleteAttachment}
      getAttachmentUrl={getAttachmentUrl}
      loading={isAddAttachmentLoading || isDeleteAttachmentLoading}
      uploadButtonProps={
        userCanEdit && {
          multiple: true,
          showUploadList: false,
          disabled: isDeleteAttachmentLoading || isAddAttachmentLoading,
          customRequest: handleAddAttachment,
          beforeUpload: handleBeforeUpload,
        }
      }
    />
  );
};

export default DocumentsBlock;
